import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { ArrowLeft, Eye, Save, FileText } from "lucide-react";
import { Button } from "@foundation/altzero_base/base/components/ui/button";
import { Input } from "@foundation/altzero_base/base/components/ui/input";
import { Label } from "@foundation/altzero_base/base/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@foundation/altzero_base/base/components/ui/card";
import { useUser } from "@foundation/altzero_base/base/contextapi/UserContext";
import { useToast } from "@foundation/altzero_base/base/hooks/use-toast";
import ScopingAILayout from "../components/ScopingAILayout";
import {
  createCustomTemplate,
  updateCustomTemplate,
  loadTemplateContent,
} from "../services/documentLibraryService";
import type { TemplateEditorConfig } from "../types/brandWorkflowConfig";
import JsonTemplateEditor from "../components/editor/JsonTemplateEditor";
import type { EditorSaveData } from "../../../altzero_plugins/features/template-editor/Editor/features/Editor/types/editor";

interface LocationState {
  importedContent?: string;
  importedTitle?: string;
  importedFileName?: string;
  editTemplate?: {
    id: string;
    title: string;
    description: string;
    content: string;
    category: string;
  };
}

// Extract sections from JSON blocks
const extractSectionsFromJsonBlocks = (blocks: any[]): string[] => {
  const sections: string[] = [];
  
  blocks.forEach((block) => {
    if (
      block.type === "heading1" ||
      block.type === "heading2" ||
      block.type === "heading3"
    ) {
      const text = block.content?.text || block.content || "";
      if (text && text.trim()) {
        sections.push(text.trim());
      }
    }
  });

  return sections.length > 0 ? sections : ["Untitled Section"];
};

export default function TemplateCreation({ config }: { config?: TemplateEditorConfig }) {
  const { user } = useUser();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const Layout = config?.layout ?? ScopingAILayout;
  const templatesListRoute = config?.routes?.templatesList ?? "/scopingai/resource-library/documents";
  const documentNewRoute = config?.routes?.documentNew ?? "/scopingai/documents/new";
  const svcCfg = config?.serviceConfig;

  const isImport = searchParams.get("import") === "true";
  const isEdit = searchParams.get("edit") === "true";
  const state = location.state as LocationState;

  // Form state
  const [templateForm, setTemplateForm] = useState({
    name: "",
    description: "",
    content: "",
    category: "",
  });

  // JSON editor state
  const [jsonEditorData, setJsonEditorData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Initialize form with imported/edit data
  useEffect(() => {
    const initializeForm = async () => {
      try {
        if (isImport && state?.importedContent) {
          // For imports, we'll create a default JSON structure
          setTemplateForm({
            name: state.importedTitle || "",
            description: `Imported from ${state.importedFileName || "MD file"}`,
            content: "",
            category: "",
          });
          // Note: Imported markdown would need conversion - for now, start fresh
          setJsonEditorData(null);
        } else if (isEdit && state?.editTemplate) {
          setIsLoading(true);
          
          try {
            // Load content from bucket
            console.log("🔄 Loading template content for editing:", state.editTemplate.id);
            const bucketContent = await loadTemplateContent(state.editTemplate.id, svcCfg);
            
            setTemplateForm({
              name: state.editTemplate.title,
              description: state.editTemplate.description,
              content: "",
              category: state.editTemplate.category || "",
            });

            // Parse JSON content
            let parsedData;
            try {
              parsedData = typeof bucketContent === "string" ? JSON.parse(bucketContent) : bucketContent;
              setJsonEditorData({
                name: state.editTemplate.title,
                description: state.editTemplate.description,
                editorState: parsedData,
                metadata: {},
              });
            } catch (e) {
              // If not valid JSON, create default structure
              console.log("⚠️ Content is not JSON, creating default structure");
              setJsonEditorData(null);
            }
            
            console.log("✅ Template content loaded successfully");
          } catch (error) {
            console.error("❌ Failed to load template content:", error);
            toast({
              title: "Error",
              description: "Failed to load template content. Using fallback data.",
              variant: "destructive",
            });
            
            setTemplateForm({
              name: state.editTemplate.title,
              description: state.editTemplate.description,
              content: "",
              category: state.editTemplate.category || "",
            });
            setJsonEditorData(null);
          } finally {
            setIsLoading(false);
          }
        }
      } catch (error) {
        console.error("❌ Error initializing form:", error);
        setIsLoading(false);
      }
    };

    initializeForm();
  }, [isImport, isEdit, state, toast]);

  // Handle save from JSON editor
  const handleJsonSave = useCallback(async (data: EditorSaveData) => {
    try {
      // Use the form name if available, otherwise use the editor's name
      const finalName = templateForm.name.trim() || data.name?.trim() || "";
      const finalDescription = templateForm.description.trim() || data.description?.trim() || "";
      
      if (!finalName) {
        toast({
          title: "Error",
          description: "Template name is required",
          variant: "destructive",
        });
        return { success: false, error: "Template name is required" };
      }

      setIsLoading(true);

      // Extract sections from JSON blocks
      const blocks = data.editorState?.blocks || [];
      const extractedSections = extractSectionsFromJsonBlocks(blocks);

      // Convert editorState to JSON string
      const jsonContent = JSON.stringify(data.editorState);

      if (isEdit && state?.editTemplate) {
        // Update existing template
        await updateCustomTemplate(state.editTemplate.id, {
          name: finalName,
          description: finalDescription,
          content: jsonContent,
          category: templateForm.category.trim(),
          sections: extractedSections,
        }, svcCfg);

        toast({
          title: "Success",
          description: "Template updated successfully",
        });

        return { success: true, id: state.editTemplate.id };
      } else {
        // Create new template
        const newTemplate = await createCustomTemplate({
          name: finalName,
          description: finalDescription,
          content: jsonContent,
          category: templateForm.category.trim(),
          userId: user?.id || "",
          sections: extractedSections,
        }, svcCfg);

        toast({
          title: "Success",
          description: "Template created successfully",
        });

        // Check if we need to return to template selection
        const createTemplateMode = sessionStorage.getItem(
          "scopingai_createTemplateMode"
        );
        const returnToStep = sessionStorage.getItem("scopingai_returnToStep");

        if (
          createTemplateMode === "true" &&
          returnToStep === "2" &&
          newTemplate?.id
        ) {
          sessionStorage.setItem(
            "selectedTemplateFromLibrary",
            String(newTemplate.id)
          );
          sessionStorage.removeItem("scopingai_createTemplateMode");
          sessionStorage.removeItem("scopingai_returnToStep");
          navigate(documentNewRoute);
          return { success: true, id: newTemplate.id };
        }

        // Redirect to templates page
        setTimeout(() => {
          navigate(templatesListRoute, { replace: true });
        }, 1500);

        return { success: true, id: newTemplate.id, data: newTemplate };
      }
    } catch (error: any) {
      console.error("Error saving template:", error);
      toast({
        title: "Error",
        description: "Failed to save template",
        variant: "destructive",
      });
      return { success: false, error: error.message || "Failed to save template" };
    } finally {
      setIsLoading(false);
    }
  }, [isEdit, state, templateForm.name, templateForm.description, templateForm.category, user?.id, toast, navigate, svcCfg, templatesListRoute, documentNewRoute]);

  return (
    <Layout>
      <div className="container mx-auto px-6 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div>
            <button
              onClick={() => {
                const createTemplateMode = sessionStorage.getItem(
                  "scopingai_createTemplateMode"
                );
                const returnToStep = sessionStorage.getItem(
                  "scopingai_returnToStep"
                );

                if (createTemplateMode === "true" && returnToStep === "2") {
                  sessionStorage.removeItem("scopingai_createTemplateMode");
                  sessionStorage.removeItem("scopingai_returnToStep");
                  navigate(documentNewRoute);
                } else {
                  navigate(templatesListRoute);
                }
              }}
              className="flex items-center text-muted-foreground hover:text-foreground mb-4"
            >
              <ArrowLeft size={16} className="mr-2" />
              {sessionStorage.getItem("scopingai_createTemplateMode") === "true"
                ? "Back to Template Selection"
                : "Back to Templates"}
            </button>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold">
                  {isEdit
                    ? "Edit Template"
                    : isImport
                    ? "Import MD Template"
                    : "Create Template"}
                </h1>
                <p className="text-muted-foreground mt-1">
                  {isImport
                    ? "Edit and preview your imported Markdown file"
                    : isEdit
                    ? "Modify your existing template"
                    : "Create a new proposal template"}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  disabled={isLoading || !templateForm.name.trim()}
                  className="gap-2"
                  type="button"
                >
                  <Save size={16} />
                  {isLoading ? "Saving..." : isEdit ? "Update Template" : "Save Template"}
                </Button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-6">
            {/* Template Form */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText size={20} />
                  Template Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="template-name">Template Name *</Label>
                    <Input
                      id="template-name"
                      placeholder="Enter template name"
                      value={templateForm.name}
                      onChange={(e) =>
                        setTemplateForm({
                          ...templateForm,
                          name: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div>
                    <Label htmlFor="template-category">Category</Label>
                    <Input
                      id="template-category"
                      placeholder="e.g., Web Development, Mobile App, etc."
                      value={templateForm.category}
                      onChange={(e) =>
                        setTemplateForm({
                          ...templateForm,
                          category: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div>
                    <Label htmlFor="template-description">Description</Label>
                    <Input
                      id="template-description"
                      placeholder="Brief description of the template"
                      value={templateForm.description}
                      onChange={(e) =>
                        setTemplateForm({
                          ...templateForm,
                          description: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* JsonTemplateEditor - JSON-based template editor */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Template Content Editor
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Create and edit your template using the visual JSON editor. Add blocks, customize layout, and use variables for dynamic content.
                </p>
              </CardHeader>
              <CardContent className="p-0">
                <div className="h-[calc(100vh-300px)] min-h-[700px] flex flex-col overflow-hidden">
                  <JsonTemplateEditor
                    key={`${templateForm.name}-${templateForm.description}`}
                    templateId={isEdit && state?.editTemplate ? state.editTemplate.id : undefined}
                    templateName={templateForm.name}
                    templateDescription={templateForm.description}
                    initialJsonData={jsonEditorData}
                    onSave={handleJsonSave}
                    onChange={(data) => {
                      if (data?.name && data.name !== templateForm.name) {
                        setTemplateForm(prev => ({
                          ...prev,
                          name: data.name,
                        }));
                      }
                      if (data?.description && data.description !== templateForm.description) {
                        setTemplateForm(prev => ({
                          ...prev,
                          description: data.description,
                        }));
                      }
                    }}
                    readonly={false}
                    className="flex-1 min-h-0"
                    isSaving={isLoading}
                    storageConfig={svcCfg}
                  />
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>
    </Layout>
  );
}
